import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Mail, RefreshCw, Search, Star } from "lucide-react";

import api from "../api";

function ratingStars(rating) {
  return Array.from({ length: 5 }, (_, index) => index < Number(rating || 0));
}

function PartyLedger() {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = `${today.slice(0, 8)}01`;

  const [customers, setCustomers] = useState([]);
  const [query, setQuery] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [view, setView] = useState("ALL");
  const [customRange, setCustomRange] = useState({ date_from: monthStart, date_to: today });
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadCustomers = useCallback(async () => {
    try {
      const res = await api.get("/accounts/invoices/meta");
      const rows = res.data.customers || [];
      setCustomers(rows);
      setSelectedCustomerId((prev) => prev || (rows.length ? String(rows[0].id) : ""));
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load customers.");
    }
  }, []);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const visibleCustomers = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return customers;
    return customers.filter((customer) =>
      [customer.name, customer.code, customer.gstin].some((value) =>
        String(value || "").toLowerCase().includes(needle)
      )
    );
  }, [customers, query]);

  const loadLedger = useCallback(async (customerId = selectedCustomerId) => {
    if (!customerId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams(
        view === "CUSTOM"
          ? { view: "ALL", ...customRange }
          : { view }
      );
      const res = await api.get(`/accounts/party/${customerId}/ledger?${params.toString()}`);
      setLedger(res.data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load party ledger.");
    } finally {
      setLoading(false);
    }
  }, [customRange, selectedCustomerId, view]);

  useEffect(() => {
    if (selectedCustomerId) {
      loadLedger(selectedCustomerId);
    }
  }, [selectedCustomerId, loadLedger]);

  const exportStatement = () => {
    if (!ledger) return;
    const printable = window.open("", "_blank", "noopener,noreferrer,width=1080,height=820");
    if (!printable) return;
    const rows = (ledger.transactions || [])
      .map(
        (txn) => `
          <tr>
            <td>${txn.date}</td>
            <td>${txn.type}</td>
            <td>${txn.reference || "-"}</td>
            <td>${txn.debit.toFixed(2)}</td>
            <td>${txn.credit.toFixed(2)}</td>
            <td>${txn.balance.toFixed(2)}</td>
          </tr>`
      )
      .join("");
    printable.document.write(`
      <html><head><title>${ledger.customer.name} Statement</title></head>
      <body style="font-family: Arial; padding: 24px;">
        <h2>${ledger.customer.name} Party Statement</h2>
        <p>Outstanding: Rs. ${Number(ledger.summary.outstanding_balance || 0).toFixed(2)}</p>
        <table border="1" cellspacing="0" cellpadding="8" width="100%">
          <thead><tr><th>Date</th><th>Type</th><th>Ref</th><th>Debit</th><th>Credit</th><th>Balance</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </body></html>
    `);
    printable.document.close();
    printable.focus();
    printable.print();
  };

  const sendStatementEmail = () => {
    if (!ledger) return;
    const subject = encodeURIComponent(`Statement for ${ledger.customer.name}`);
    const body = encodeURIComponent(
      `Customer: ${ledger.customer.name}\nOutstanding: Rs. ${Number(ledger.summary.outstanding_balance || 0).toFixed(2)}\nOverdue: Rs. ${Number(ledger.summary.overdue_amount || 0).toFixed(2)}`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <div className="space-y-6">
      {error && <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

      <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-6 py-5">
          <h3 className="text-xl font-bold text-slate-900">Party Ledger</h3>
          <p className="text-sm text-slate-500">Customer-wise passbook for invoices, payments, credits, and running balances.</p>
        </div>
        <div className="grid gap-4 p-6 lg:grid-cols-[320px_1fr]">
          <div className="space-y-3">
            <div className="relative">
              <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} className="input pl-9" placeholder="Search customer" />
            </div>
            <div className="max-h-[420px] overflow-y-auto rounded-2xl border border-slate-200">
              {visibleCustomers.map((customer) => (
                <button
                  key={customer.id}
                  onClick={() => setSelectedCustomerId(String(customer.id))}
                  className={`block w-full border-b border-slate-100 px-4 py-3 text-left last:border-b-0 ${String(customer.id) === String(selectedCustomerId) ? "bg-indigo-50" : "bg-white"}`}
                >
                  <div className="font-semibold text-slate-900">{customer.name}</div>
                  <div className="text-xs text-slate-500">{customer.code} {customer.gstin ? `• ${customer.gstin}` : ""}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            {ledger && (
              <>
                <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
                  <SummaryCard label="Name" value={ledger.customer.name} />
                  <SummaryCard label="GSTIN" value={ledger.customer.gstin || "Non-GST"} />
                  <SummaryCard label="Outstanding" value={`Rs. ${Number(ledger.summary.outstanding_balance || 0).toFixed(2)}`} />
                  <SummaryCard label="Overdue" value={`Rs. ${Number(ledger.summary.overdue_amount || 0).toFixed(2)}`} tone="rose" />
                  <SummaryCard
                    label="Rating"
                    value={
                      <span className="flex items-center gap-1">
                        {ratingStars(ledger.customer.payment_rating).map((filled, index) => (
                          <Star key={index} size={14} className={filled ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
                        ))}
                      </span>
                    }
                  />
                </section>

                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap gap-2">
                    {["ALL", "DAY", "WEEK", "MONTH", "CUSTOM"].map((tab) => (
                      <button key={tab} onClick={() => setView(tab)} className={`tab-btn ${view === tab ? "tab-btn-active" : ""}`}>{tab}</button>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => loadLedger()} className="secondary-btn"><RefreshCw size={15} /> Refresh</button>
                    <button onClick={exportStatement} className="secondary-btn">Download Statement PDF</button>
                    <button onClick={sendStatementEmail} className="secondary-btn"><Mail size={15} /> Send Statement Email</button>
                  </div>
                </div>

                {view === "CUSTOM" && (
                  <div className="grid gap-3 md:grid-cols-3">
                    <input type="date" value={customRange.date_from} onChange={(e) => setCustomRange((prev) => ({ ...prev, date_from: e.target.value }))} className="input" />
                    <input type="date" value={customRange.date_to} onChange={(e) => setCustomRange((prev) => ({ ...prev, date_to: e.target.value }))} className="input" />
                    <button onClick={() => loadLedger()} className="secondary-btn">Apply Range</button>
                  </div>
                )}

                <div className="overflow-x-auto rounded-2xl border border-slate-200">
                  <table className="min-w-full text-left text-sm">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-4 py-3">Date</th>
                        <th className="px-4 py-3">Type</th>
                        <th className="px-4 py-3">Ref</th>
                        <th className="px-4 py-3">Debit</th>
                        <th className="px-4 py-3">Credit</th>
                        <th className="px-4 py-3">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(ledger.transactions || []).map((txn) => (
                        <tr key={txn.id} className={`border-t border-slate-100 ${txn.is_overdue ? "bg-rose-50" : ""}`}>
                          <td className="px-4 py-3">{txn.date}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span>{txn.type}</span>
                              {txn.is_overdue && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700">{txn.days_overdue} days overdue</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3">{txn.reference || "-"}</td>
                          <td className="px-4 py-3 font-semibold text-emerald-700">Rs. {Number(txn.debit || 0).toFixed(2)}</td>
                          <td className="px-4 py-3 font-semibold text-sky-700">Rs. {Number(txn.credit || 0).toFixed(2)}</td>
                          <td className="px-4 py-3 font-semibold">Rs. {Number(txn.balance || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {loading && <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-500">Loading party ledger...</div>}
          </div>
        </div>
      </section>

      <style>{`
        .input {
          width: 100%;
          border-radius: 0.75rem;
          border: 1px solid rgb(203 213 225);
          padding: 0.65rem 0.85rem;
          background: white;
        }
        .secondary-btn, .tab-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          border-radius: 0.75rem;
          padding: 0.65rem 1rem;
          font-size: 0.875rem;
          font-weight: 600;
          border: 1px solid rgb(203 213 225);
          background: white;
          color: rgb(51 65 85);
        }
        .tab-btn-active {
          background: rgb(79 70 229);
          border-color: rgb(79 70 229);
          color: white;
        }
      `}</style>
    </div>
  );
}

function SummaryCard({ label, value, tone = "slate" }) {
  const toneClass = tone === "rose" ? "text-rose-700" : "text-slate-900";
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-2 text-sm font-semibold ${toneClass}`}>{value}</div>
    </div>
  );
}

export default PartyLedger;
